/**
 * AdsRangeSelector — shared terminal-styled timeframe picker.
 *
 * Presets (today, yesterday, last 7/30/90 days, this month) + a custom from/to. Emits a
 * normalized AdsRange; the parent owns the value and propagates it to every
 * range-aware surface. Reuses the .gads-bar__picker SCSS so it matches the
 * account dropdown exactly.
 */

import React from 'react';
import { AdsRange, AdsRangePreset, presetRange } from './googleAdsApi';

const PRESETS: Array<{ key: AdsRangePreset; label: string }> = [
  { key: 'today', label: 'today' },
  { key: 'yesterday', label: 'yesterday' },
  { key: '3d', label: 'last 3 days' },
  { key: '7d', label: 'last 7 days' },
  { key: '30d', label: 'last 30 days' },
  { key: '90d', label: 'last 90 days' },
  { key: 'month', label: 'this month' },
  { key: 'custom', label: 'custom…' },
];

export const AdsRangeSelector: React.FC<{
  value: AdsRange;
  onChange: (range: AdsRange) => void;
}> = ({ value, onChange }) => {
  const handlePreset = (preset: AdsRangePreset) => {
    // Switching to custom keeps the current window as the starting point.
    onChange(preset === 'custom' ? presetRange('custom', value.from, value.to) : presetRange(preset));
  };

  return (
    <div className="gads-range">
      <select
        className="gads-bar__picker gads-range__preset"
        value={value.preset}
        onChange={(e) => handlePreset(e.target.value as AdsRangePreset)}
        title="Timeframe"
      >
        {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
      </select>
      {value.preset === 'custom' && (
        <>
          <input
            type="date"
            className="gads-range__date"
            value={value.from}
            max={value.to}
            onChange={(e) => onChange(presetRange('custom', e.target.value, value.to))}
            title="From"
          />
          <span className="gads-range__sep">→</span>
          <input
            type="date"
            className="gads-range__date"
            value={value.to}
            min={value.from}
            onChange={(e) => onChange(presetRange('custom', value.from, e.target.value))}
            title="To"
          />
        </>
      )}
    </div>
  );
};

export default AdsRangeSelector;
